/**
 * "Brush up" and "Check my lesson" links from a tagged day item (R7, R8, KTD4, KTD5, KTD9). Pure.
 *
 * Each link opens `/from-planner` on the other app's host with the hand-off in the fragment, so
 * it never reaches a Worker log, a referrer, or PostHog's `$current_url`. The two fragments
 * mirror each app's own builder exactly: One Step Ahead's `plannerHandoffFragment`
 * (`packages/one-step-ahead/src/core/planner-handoff.ts`) and Collab's `plannerFragment`
 * (`packages/collab/src/planner-handoff.ts`). The Planner's state, grade, and subject values are
 * the Learning Commons labels both apps accept, so they pass unchanged.
 */
import { standardLabel, type Course, type CourseStandard } from './course';
import { MAX_ITEM_TAGS } from './days';

export const ONE_STEP_AHEAD_ORIGIN = 'https://ahead.labs.240.org';
export const COLLAB_ORIGIN = 'https://collab.labs.240.org';
export const HANDOFF_PATH = '/from-planner';
/** One Step Ahead's ask cap (`MAX_ASK_LENGTH` in its `src/core/ask.ts`). */
export const MAX_ASK_TEXT = 4000;

type Tagged = Pick<CourseStandard, 'id' | 'code' | 'statement'>;

/**
 * KTD9: every code on the first line, then each statement in tag order while it fits. The
 * statements stop at the first that would pass the cap, so the text never skips one.
 */
export function brushUpText(standards: readonly Tagged[]): string {
  const tagged = standards.slice(0, MAX_ITEM_TAGS);
  let text = tagged.map(standardLabel).join(', ').slice(0, MAX_ASK_TEXT);
  for (const s of tagged) {
    const next = `${text}\n\n${s.code ? `${s.code}: ` : ''}${s.statement}`;
    if (next.length > MAX_ASK_TEXT) break;
    text = next;
  }
  return text;
}

/** The "Brush up" link: One Step Ahead's ask form, filled in and never sent (R7, KTD9). */
export function brushUpLink(course: Course, standards: readonly Tagged[]): string {
  const pairs: [string, string][] = [
    ['v', '1'], ['text', brushUpText(standards)], ['state', course.state], ['grade', course.grade], ['subject', course.subject], ['from', 'planner'],
  ];
  return `${ONE_STEP_AHEAD_ORIGIN}${HANDOFF_PATH}#${pairs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}`;
}

/** An attached Collab material, by its group and id: Collab opens its page (planner collab materials KTD6). */
export interface CheckTarget { group: string; material: string }

/**
 * The "Check my lesson" link: Collab's standards check with these standards expected and the
 * item's link for reference (R8, KTD5). Each list element is encoded, then joined by a literal
 * comma. A standard with no stored code is named by its id, which Collab accepts as a code.
 * With a `target`, Collab opens that material's page with the standards expected (planner
 * collab materials R8, KTD6); a Collab without that knows nothing of the keys and opens home.
 */
export function lessonCheckLink(course: Course, standards: readonly Tagged[], link: string | null, target?: CheckTarget): string {
  const tagged = standards.slice(0, MAX_ITEM_TAGS);
  const list = (values: string[]) => values.map(encodeURIComponent).join(',');
  const parts = [
    'v=1', 'from=planner',
    `ids=${list(tagged.map(s => s.id))}`,
    `codes=${list(tagged.map(s => s.code || s.id))}`,
    `state=${encodeURIComponent(course.state)}`,
    `subject=${encodeURIComponent(course.subject)}`,
    `grade=${encodeURIComponent(course.grade)}`,
  ];
  if (link) parts.push(`link=${encodeURIComponent(link)}`);
  if (target) parts.push(`group=${encodeURIComponent(target.group)}`, `material=${encodeURIComponent(target.material)}`);
  return `${COLLAB_ORIGIN}${HANDOFF_PATH}#${parts.join('&')}`;
}
